/**
 * Driver-level monthly overrides — the driver sub-rows under an expanded
 * account in the P&L grid.
 *
 * Expand an account to its driver(s). Key a new rate into a month and the
 * driver stays in force: dollars keep recalculating as occupancy and revenue
 * move. On Labor Config positions the wage rate is editable too, so a raise
 * can start in one month and carry forward.
 *
 * Contrast with the value override keyed on the ACCOUNT row (forecastOverride):
 * that holds an exact dollar amount and pauses the driver.
 *
 * Row sources:
 *   - single-rate drivers ($ / ORN, % of Selected Revenue, Budgeted $POR, …):
 *     one row from account.monthlyDriverRate (engine RATE_SPECS)
 *   - Zero Base: one row per layer (account.monthlyZeroBaseLayers)
 *   - Labor Config: one row per position + wage line (account.monthlyLaborConfigs)
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type {
  ForecastAccount,
  DriverInputOverrideItem,
  DriverInputOverrideField,
  ZeroBaseDriverLayer,
  LaborPositionConfig,
} from '../../types';
import { MONTHS, MONTH_LABELS } from '../../types';
import { formatCurrency } from '../../utils/formatters';

// ============ Types ============

export type RateUnit = 'currency' | 'percent' | 'minutes' | 'hours' | 'fte' | 'fixed' | 'wage';

export interface DriverSubRowCell {
  rate: number | null;
  text: string;
  overridden: boolean;
  baseText?: string;
  /** Labor positions costed by hours: the hourly wage line under the rate */
  wage?: { rate: number | null; text: string; overridden: boolean; baseText?: string };
  /** Dollars this driver/layer/position produced this month (when the engine exposes it) */
  amount?: number | null;
  /** Rolled-in actual month: the rate shown is realized (actual $ ÷ actual basis) */
  isActual?: boolean;
}

export interface DriverSubRow {
  key: string;
  layerKey: string;
  field: DriverInputOverrideField;
  label: string;
  sub: string;
  badge: string;
  unit: RateUnit;
  hasWage: boolean;
  cells: Record<string, DriverSubRowCell>; // keyed by month key (jan..dec)
  total: number | null;
  /** No value, override or dollars in any month — hidden behind "Show more" */
  isEmpty?: boolean;
}

function rowIsEmpty(cells: Record<string, DriverSubRowCell>, total: number | null): boolean {
  if (total) return false;
  return !Object.values(cells).some(c =>
    (c.rate !== null && c.rate !== undefined && c.rate !== 0) || c.overridden || !!c.wage?.overridden || !!c.amount
  );
}

export type OverrideScope = 'month' | 'forward' | 'all';

// ============ Formatting ============

function currencySymbolFor(currency?: string | null): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })
      .formatToParts(0).find(p => p.type === 'currency')?.value || '$';
  } catch {
    return '$';
  }
}

export function formatRate(unit: RateUnit, rate: number | null | undefined, currency?: string | null): string {
  if (rate === null || rate === undefined || !Number.isFinite(rate)) return unit === 'wage' ? 'auto' : '—';
  const sym = currencySymbolFor(currency);
  switch (unit) {
    case 'currency':
      return `${sym}${rate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    case 'percent':
      return `${rate.toFixed(2)}%`;
    case 'minutes':
      return `${rate.toFixed(1)} min`;
    case 'hours':
      return `${rate.toFixed(2)} hrs`;
    case 'fte':
      return `${rate.toFixed(2)} FTE`;
    case 'fixed':
      return `${sym}${Math.round(rate).toLocaleString('en-US')}/mo`;
    case 'wage':
      return `${sym}${rate.toFixed(2)}/hr`;
    default:
      return String(rate);
  }
}

function unitHint(unit: RateUnit): string {
  switch (unit) {
    case 'currency': return 'Rate ($)';
    case 'percent': return 'Rate (%)';
    case 'minutes': return 'Minutes per occupied room';
    case 'hours': return 'Hours per occupied room';
    case 'fte': return 'Full-time equivalents';
    case 'fixed': return 'Fixed $ per month';
    case 'wage': return 'Hourly wage ($/hr)';
    default: return 'Value';
  }
}

const LAYER_BADGE: Record<string, { badge: string; unit: RateUnit; sub: string }> = {
  fixed: { badge: 'fixed $', unit: 'fixed', sub: 'Fixed, every month' },
  pct_revenue: { badge: '% × total rev', unit: 'percent', sub: 'Share of total revenue' },
  pct_other: { badge: '% × accounts', unit: 'percent', sub: 'Share of source accounts' },
  por: { badge: '$ × ORN', unit: 'currency', sub: 'Scales with occupied rooms' },
  par: { badge: '$ × ARN', unit: 'currency', sub: 'Scales with available rooms' },
};

const POSITION_BADGE: Record<string, { badge: string; unit: RateUnit; sub: string }> = {
  min_por: { badge: 'min per ORN', unit: 'minutes', sub: 'Minutes per occupied room × wage' },
  hrs_por: { badge: 'hrs per ORN', unit: 'hours', sub: 'Hours per occupied room × wage' },
  fte: { badge: 'FTE', unit: 'fte', sub: 'Fixed staffing × wage' },
  pct_revenue: { badge: '% × revenue', unit: 'percent', sub: 'Share of revenue' },
};

const BASIS_LABEL: Record<string, string> = {
  orn: 'occupied rooms',
  arn: 'available rooms',
  total_revenue: 'total revenue',
  selected_revenue: 'selected revenue',
  source_accounts: 'source accounts',
  ly_actual: 'last year actual',
};

// ============ Row building ============

/** Stable: rows with a value first, empty ones after (original order within each group). */
function sortEmptyLast(rows: DriverSubRow[]): DriverSubRow[] {
  return [...rows.filter(r => !r.isEmpty), ...rows.filter(r => r.isEmpty)];
}

/** Whether the account has drivers a user can override month-by-month. */
export function isDriverExpandable(account: ForecastAccount): boolean {
  if (account.driverType === 'Zero Base') return !!account.zeroBaseDriverLayers?.length;
  if (account.driverType === 'Labor Config') return !!account.laborPositionConfigs?.length;
  const rates = account.monthlyDriverRate;
  return !!rates && Object.values(rates).some(r => !!r);
}

export function buildDriverSubRows(account: ForecastAccount, currency?: string | null): DriverSubRow[] {
  const rows: DriverSubRow[] = [];

  if (account.driverType === 'Zero Base' && account.zeroBaseDriverLayers?.length) {
    for (const layer of account.zeroBaseDriverLayers) {
      const meta = LAYER_BADGE[layer.type] || { badge: layer.type, unit: 'currency' as RateUnit, sub: '' };
      const cells: Record<string, DriverSubRowCell> = {};
      let total = 0;
      let hasTotal = false;
      MONTHS.forEach(m => {
        const eff: ZeroBaseDriverLayer | undefined = account.monthlyZeroBaseLayers?.[m]?.find(l => l.id === layer.id);
        const rate = eff?.value ?? layer.value ?? null;
        const overridden = !!eff?.overrides && 'value' in eff.overrides;
        const amount = account.explanationFactors?.[m]?.layerResults?.find(r => r.id === layer.id)?.amount;
        if (typeof amount === 'number') { total += amount; hasTotal = true; }
        cells[m] = {
          rate,
          text: formatRate(meta.unit, rate, currency),
          overridden,
          baseText: overridden ? formatRate(meta.unit, eff?.overrides?.value ?? null, currency) : undefined,
          amount: typeof amount === 'number' ? amount : null,
        };
      });
      rows.push({
        key: `zb:${layer.id}`,
        layerKey: String(layer.id),
        field: 'value',
        label: layer.description || `Layer ${layer.id}`,
        sub: meta.sub,
        badge: meta.badge,
        unit: meta.unit,
        hasWage: false,
        cells,
        total: hasTotal ? total : null,
        isEmpty: rowIsEmpty(cells, hasTotal ? total : null),
      });
    }
    return sortEmptyLast(rows);
  }

  if (account.driverType === 'Labor Config' && account.laborPositionConfigs?.length) {
    for (const cfg of account.laborPositionConfigs) {
      const meta = POSITION_BADGE[cfg.driverType] || { badge: cfg.driverType, unit: 'currency' as RateUnit, sub: '' };
      const hasWage = cfg.driverType !== 'pct_revenue';
      const cells: Record<string, DriverSubRowCell> = {};
      let total = 0;
      let hasTotal = false;
      MONTHS.forEach(m => {
        const eff: LaborPositionConfig | undefined = account.monthlyLaborConfigs?.[m]?.find(c => c.positionId === cfg.positionId);
        const rate = eff?.value ?? cfg.value ?? null;
        const overridden = !!eff?.overrides && 'value' in eff.overrides;
        const wageRate = eff?.hourlyRate ?? cfg.hourlyRate ?? null;
        const wageOverridden = !!eff?.overrides && 'hourlyRate' in eff.overrides;
        const posResult = (account.explanationFactors?.[m] as { positionResults?: Array<{ positionId: number; amount: number; rate?: number | null }> } | undefined)
          ?.positionResults?.find(r => r.positionId === cfg.positionId);
        const amount = posResult?.amount;
        if (typeof amount === 'number') { total += amount; hasTotal = true; }
        // When no explicit wage is saved the engine resolves one from payroll —
        // show what it actually costed with, so a wage override has a baseline.
        const resolvedWage = wageRate ?? (typeof posResult?.rate === 'number' ? posResult.rate : null);
        cells[m] = {
          rate,
          text: formatRate(meta.unit, rate, currency),
          overridden,
          baseText: overridden ? formatRate(meta.unit, eff?.overrides?.value ?? null, currency) : undefined,
          amount: typeof amount === 'number' ? amount : null,
          wage: hasWage ? {
            rate: resolvedWage,
            text: formatRate('wage', resolvedWage, currency),
            overridden: wageOverridden,
            baseText: wageOverridden ? formatRate('wage', eff?.overrides?.hourlyRate ?? null, currency) : undefined,
          } : undefined,
        };
      });
      rows.push({
        key: `lc:${cfg.positionId}`,
        layerKey: String(cfg.positionId),
        field: 'value',
        label: cfg.positionName || `Position ${cfg.positionId}`,
        sub: meta.sub,
        badge: meta.badge,
        unit: meta.unit,
        hasWage,
        cells,
        total: hasTotal ? total : null,
        isEmpty: rowIsEmpty(cells, hasTotal ? total : null),
      });
    }
    return sortEmptyLast(rows);
  }

  // Single-rate driver (input or derived)
  const rates = account.monthlyDriverRate || {};
  const anyRate = MONTHS.map(m => rates[m]).find(r => !!r);
  if (!anyRate) return rows;
  const unit: RateUnit = anyRate.unit === 'percent' ? 'percent' : 'currency';
  const cells: Record<string, DriverSubRowCell> = {};
  MONTHS.forEach(m => {
    const r = rates[m];
    // Closed months show actual dollars on the account row, so the sub-row
    // shows the REALIZED rate (actual $ ÷ actual rooms/revenue), not the
    // rate the driver had forecast for that month.
    const rolledActual = !!account.monthlyExpanded?.[m]?.isActualRolledIn;
    const useActual = rolledActual && r?.actualRate != null;
    cells[m] = {
      rate: useActual ? (r?.actualRate ?? null) : (r?.rate ?? null),
      text: formatRate(unit, useActual ? (r?.actualRate ?? null) : (r?.rate ?? null), currency),
      overridden: !useActual && !!r?.overridden,
      baseText: !useActual && r?.overridden ? formatRate(unit, r.baseRate, currency) : undefined,
      amount: account.monthlyExpanded?.[m]?.baseAmount ?? null,
      isActual: useActual,
    };
  });
  const basis = anyRate.basis ? BASIS_LABEL[anyRate.basis] || anyRate.basis : null;
  rows.push({
    key: 'rate',
    layerKey: '',
    field: 'input',
    label: anyRate.label || String(account.driverType),
    sub: anyRate.mode === 'derived'
      ? `Derived each month${basis ? ` × ${basis}` : ''} — override keeps the driver live`
      : basis ? `Rate × ${basis}` : 'Rate',
    badge: unit === 'percent' ? `% × ${basis || 'basis'}` : `$ × ${basis || 'basis'}`,
    unit,
    hasWage: false,
    cells,
    total: account.total,
  });
  return rows;
}

// ============ Editor popover ============

interface EditorState {
  rowKey: string;
  monthIndex: number;
  field: DriverInputOverrideField;
  unit: RateUnit;
  current: number | null;
  canRevert: boolean;
  anchor: { top: number; left: number; width: number };
}

interface RateEditorPopoverProps {
  state: EditorState;
  scope: OverrideScope;
  onScope: (s: OverrideScope) => void;
  draft: string;
  onDraft: (v: string) => void;
  onApply: () => void;
  onCancel: () => void;
  onRevert: () => void;
  scopeLabels: { month: string; forward: string; all: string };
  disabledScopes: Partial<Record<OverrideScope, boolean>>;
}

function RateEditorPopover({ state, scope, onScope, draft, onDraft, onApply, onCancel, onRevert, scopeLabels, disabledScopes }: RateEditorPopoverProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: state.anchor.top, left: state.anchor.left });

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  // Keep the popover inside the viewport
  useEffect(() => {
    const el = popRef.current;
    if (!el) return;
    const width = 244;
    const rect = el.getBoundingClientRect();
    let left = state.anchor.left + state.anchor.width - width;
    let top = state.anchor.top;
    if (left < 8) left = 8;
    if (left + width > window.innerWidth - 8) left = window.innerWidth - width - 8;
    if (top + rect.height > window.innerHeight - 8) top = Math.max(8, window.innerHeight - rect.height - 8);
    setPos({ top, left });
  }, [state.anchor]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) onCancel();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [onCancel]);

  const title = state.field === 'hourlyRate' ? 'Change wage rate'
    : state.field === 'input' ? 'Override driver rate' : 'Override layer value';
  const hint = state.field === 'hourlyRate'
    ? 'Hours stay driven by occupancy. A raise normally starts in one month and carries forward.'
    : 'The driver stays assigned. Dollars recalculate from this rate as occupancy and revenue move.';

  const scopes: { id: OverrideScope; label: string }[] = [
    { id: 'month', label: scopeLabels.month },
    { id: 'forward', label: scopeLabels.forward },
    { id: 'all', label: scopeLabels.all },
  ];

  return createPortal(
    <div
      ref={popRef}
      className="fv3-root fixed z-[9999]"
      style={{ top: pos.top, left: pos.left, width: 244 }}
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
    >
      <input
        ref={inputRef}
        value={draft}
        onChange={e => onDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); onApply(); }
        }}
        placeholder={unitHint(state.unit)}
        className="w-full box-border px-2.5 py-1.5 rounded-lg text-right text-[13px] tabular-nums outline-none bg-white text-slate-900"
        style={{ border: '1px solid var(--lodge-orange-400)', boxShadow: '0 0 0 3px var(--fv3-accent-tint, rgba(255,122,0,0.12))', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
      />
      <div className="mt-1.5 p-3 bg-white rounded-xl border border-slate-200 text-left" style={{ boxShadow: '0 8px 24px rgba(15,15,17,0.12)' }}>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{title}</div>
        <div className="mt-1 text-xs text-slate-600" style={{ textWrap: 'pretty' } as React.CSSProperties}>{hint}</div>
        <div className="mt-2.5 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{unitHint(state.unit)}</div>
        <div className="flex flex-col gap-1 mt-1.5">
          {scopes.map(s => {
            const active = scope === s.id;
            const disabled = !!disabledScopes[s.id];
            return (
              <button
                key={s.id}
                type="button"
                disabled={disabled}
                onClick={() => onScope(s.id)}
                className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs transition-colors ${
                  active ? 'font-semibold' : 'font-medium text-slate-600 hover:bg-slate-50'
                } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                style={active
                  ? { border: '1px solid var(--lodge-orange-400)', background: 'var(--lodge-orange-50)', color: 'var(--lodge-orange-500)' }
                  : { border: '1px solid var(--lodge-onyx-100, #E8E8F0)', background: '#fff' }}
              >
                {s.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button
            type="button"
            onClick={onApply}
            className="px-3.5 py-2 rounded-xl text-xs font-semibold text-white"
            style={{ background: 'var(--lodge-orange-400)' }}
          >
            Apply
          </button>
          <button type="button" onClick={onCancel} className="px-3 py-2 rounded-xl text-xs font-medium text-slate-600 hover:bg-slate-50">
            Cancel
          </button>
          {state.canRevert && (
            <button type="button" onClick={onRevert} className="ml-auto text-xs font-semibold" style={{ color: 'var(--lodge-orange-400)' }}>
              Revert
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ============ Sub-rows ============

interface DriverSubRowsProps {
  account: ForecastAccount;
  rows: DriverSubRow[];
  year: number;
  currency?: string | null;
  isBulkMode?: boolean;
  isReadOnly?: boolean;
  isMonthEditable: (monthIndex: number) => boolean;
  /** How many <td>s a month occupies (expanded-column view) */
  monthColSpan: (monthIndex: number) => number;
  /** How many <td>s the Total column occupies (1 unless expanded) */
  totalColSpan?: number;
  activeMonth?: number | null;
  onSave: (items: DriverInputOverrideItem[]) => Promise<unknown>;
  defaultScope?: OverrideScope;
  /** Labor Config rows: clicking the position name opens the position-driver modal */
  onOpenPositionConfig?: (account: ForecastAccount) => void;
}

export function DriverSubRows({
  account, rows, year, currency, isBulkMode = false, isReadOnly = false,
  isMonthEditable, monthColSpan, totalColSpan = 1, activeMonth, onSave, defaultScope = 'month', onOpenPositionConfig,
}: DriverSubRowsProps) {
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [draft, setDraft] = useState('');
  const [scope, setScope] = useState<OverrideScope>(defaultScope);
  const [pending, setPending] = useState<string | null>(null);
  // Positions/layers with nothing budgeted or forecast stay collapsed
  const [showEmpty, setShowEmpty] = useState(false);
  const emptyCount = rows.filter(r => r.isEmpty).length;
  const visibleRows = showEmpty ? rows : rows.filter(r => !r.isEmpty);
  const stickyColSpan = isBulkMode ? 2 : 1;

  const editableMonths = useMemo(() => MONTHS.map((_, i) => isMonthEditable(i)), [isMonthEditable]);

  const monthsForScope = (s: OverrideScope, monthIndex: number): number[] => {
    if (s === 'month') return editableMonths[monthIndex] ? [monthIndex] : [];
    if (s === 'forward') return MONTHS.map((_, i) => i).filter(i => i >= monthIndex && editableMonths[i]);
    return MONTHS.map((_, i) => i).filter(i => editableMonths[i]);
  };

  const openEditor = (
    e: React.MouseEvent<HTMLElement>,
    row: DriverSubRow,
    monthIndex: number,
    field: DriverInputOverrideField,
    unit: RateUnit,
    current: number | null,
    canRevert: boolean
  ) => {
    e.stopPropagation();
    if (isReadOnly || !editableMonths[monthIndex]) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setEditor({
      rowKey: row.key, monthIndex, field, unit, current, canRevert,
      anchor: { top: rect.top, left: rect.left, width: rect.width },
    });
    setDraft(current === null || current === undefined ? '' : String(Number(current.toFixed(4))));
    setScope(field === 'hourlyRate' ? 'forward' : defaultScope);
  };

  const closeEditor = () => setEditor(null);

  const submit = async (items: DriverInputOverrideItem[], label: string) => {
    if (items.length === 0) return;
    setPending(label);
    try {
      await onSave(items);
    } finally {
      setPending(null);
    }
  };

  const apply = async () => {
    if (!editor) return;
    const row = rows.find(r => r.key === editor.rowKey);
    if (!row) return;
    const raw = parseFloat(String(draft).replace(/[^0-9.-]/g, ''));
    if (!Number.isFinite(raw)) { closeEditor(); return; }
    const months = monthsForScope(scope, editor.monthIndex);
    const items: DriverInputOverrideItem[] = months.map(mi => ({
      chartOfAccountId: account.chartOfAccountId,
      year,
      month: mi + 1,
      layerKey: row.layerKey,
      field: editor.field === 'hourlyRate' ? 'hourlyRate' : row.field,
      value: raw,
    }));
    closeEditor();
    await submit(items, `${row.key}:${editor.monthIndex}`);
  };

  const revert = async (row: DriverSubRow, monthIndex: number, field: DriverInputOverrideField) => {
    closeEditor();
    await submit([{
      chartOfAccountId: account.chartOfAccountId,
      year,
      month: monthIndex + 1,
      layerKey: row.layerKey,
      field,
      value: null,
    }], `${row.key}:${monthIndex}`);
  };

  const monthLabel = editor ? MONTH_LABELS[editor.monthIndex] : '';
  const scopeLabels = {
    month: `This month only (${monthLabel})`,
    forward: `${monthLabel} and forward`,
    all: 'All open months',
  };
  const disabledScopes = editor ? {
    forward: monthsForScope('forward', editor.monthIndex).length <= 1,
    all: monthsForScope('all', editor.monthIndex).length <= 1,
  } : {};

  return (
    <>
      {visibleRows.map(row => (
        <tr
          key={row.key}
          className="fv3-r-driver border-b border-slate-100 transition-colors"
          data-driver-row={row.key}
          style={{ background: 'var(--lodge-onyx-50, #FBFBFD)' }}
        >
          {isBulkMode && <td className="sticky left-0 z-20" style={{ background: 'var(--lodge-onyx-50, #FBFBFD)' }} />}
          <td
            className={`fv3-driver-label ${isBulkMode ? '' : 'sticky left-0'} py-1.5 px-4 z-20`}
            style={{ background: 'var(--lodge-onyx-50, #FBFBFD)', paddingLeft: '4.5rem' }}
          >
            <div className="flex items-start gap-2.5" style={{ maxWidth: 190 }} title={`${row.label} — ${row.sub}`}>
              <span className="w-0.5 self-stretch rounded-sm bg-slate-200 flex-shrink-0" style={{ minHeight: 26 }} />
              <div className="min-w-0 flex-1">
                {onOpenPositionConfig && row.key.startsWith('lc:') ? (
                  <button
                    type="button"
                    onClick={e => { e.stopPropagation(); onOpenPositionConfig(account); }}
                    className="block max-w-full text-left text-xs font-medium text-slate-700 truncate hover:underline"
                    style={{ textDecorationColor: 'var(--lodge-orange-400)' }}
                    title={`Edit ${row.label} driver`}
                  >
                    {row.label}
                  </button>
                ) : (
                  <div className="text-xs font-medium text-slate-700 truncate">{row.label}</div>
                )}
                <div className="mt-0.5 flex items-center gap-1.5 min-w-0">
                  <span className="px-1.5 py-0.5 rounded bg-white border border-slate-200 text-slate-600 text-[10px] font-medium whitespace-nowrap flex-shrink-0">
                    {row.badge}
                  </span>
                  <span className="text-[11px] text-slate-500 truncate">{row.sub}</span>
                </div>
              </div>
            </div>
          </td>
          {MONTHS.map((m, i) => {
            const cell = row.cells[m];
            const editable = !isReadOnly && editableMonths[i];
            const isPending = pending === `${row.key}:${i}`;
            const isActiveCol = activeMonth === i + 1;
            const span = monthColSpan(i);
            const overrideTitle = cell.isActual
              ? `Actual rate for ${MONTH_LABELS[i]} (actual $ ÷ actual basis)`
              : cell.overridden
                ? `Rate overridden for ${MONTH_LABELS[i]} · ${cell.baseText} → ${cell.text} · driver still live`
                : editable ? `Click to override this rate for ${MONTH_LABELS[i]} — the driver keeps applying it` : 'Not editable';
            return (
              <td
                key={m}
                colSpan={span > 1 ? span : undefined}
                className={`fv3-driver-cell relative text-right py-1.5 px-3 tabular-nums align-top text-xs ${editable ? 'cursor-pointer' : 'cursor-default'} ${isPending ? 'opacity-60' : ''}`}
                style={isActiveCol ? { background: 'color-mix(in srgb, var(--lodge-orange-50) 50%, transparent)' } : undefined}
                title={overrideTitle}
                onClick={e => openEditor(e, row, i, row.field, row.unit, cell.rate, cell.overridden)}
                onMouseEnter={e => { if (editable) (e.currentTarget as HTMLTableCellElement).style.background = 'var(--lodge-orange-50)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLTableCellElement).style.background = isActiveCol ? 'color-mix(in srgb, var(--lodge-orange-50) 50%, transparent)' : ''; }}
              >
                {cell.overridden && (
                  <span className="fv3-mark-dot absolute top-1 right-1 w-1.5 h-1.5 rounded-full" style={{ background: 'var(--lodge-orange-400)' }} />
                )}
                <span className={cell.isActual ? 'text-slate-500' : 'text-slate-700'} style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{cell.text}</span>
                {cell.overridden && (
                  <div className="mt-0.5 text-[10px] text-slate-500 leading-tight">was {cell.baseText}</div>
                )}
                {row.hasWage && cell.wage && (
                  <div
                    className="relative mt-0.5 pl-1 pr-0.5 rounded text-[11px] text-slate-500 leading-tight"
                    style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                    title={cell.wage.overridden
                      ? `Wage overridden for ${MONTH_LABELS[i]} · ${cell.wage.baseText} → ${cell.wage.text}`
                      : editable ? `Click to change the wage rate for ${MONTH_LABELS[i]} — apply a raise and push it forward` : undefined}
                    onClick={e => openEditor(e, row, i, 'hourlyRate', 'wage', cell.wage?.rate ?? null, !!cell.wage?.overridden)}
                    onMouseEnter={e => { if (editable) { (e.currentTarget as HTMLElement).style.background = 'var(--fv3-accent-wash, #FFF1E0)'; (e.currentTarget as HTMLElement).style.color = 'var(--lodge-orange-500)'; } }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = ''; (e.currentTarget as HTMLElement).style.color = ''; }}
                  >
                    {cell.wage.overridden && (
                      <span className="fv3-mark-dot absolute top-1 -right-0.5 w-1.5 h-1.5 rounded-full" style={{ background: 'var(--lodge-orange-400)' }} />
                    )}
                    {cell.wage.text}
                    {cell.wage.overridden && <span className="ml-1 text-[10px] text-slate-400">was {cell.wage.baseText}</span>}
                  </div>
                )}
                {(cell.overridden || cell.wage?.overridden) && editable && (
                  <div className="mt-0.5 flex justify-end gap-2 text-[10px] font-semibold leading-tight">
                    {cell.overridden && (
                      <button type="button" className="underline" style={{ color: 'var(--lodge-orange-400)' }}
                        onClick={e => { e.stopPropagation(); void revert(row, i, row.field); }}>
                        Revert
                      </button>
                    )}
                    {cell.wage?.overridden && (
                      <button type="button" className="underline" style={{ color: 'var(--lodge-orange-400)' }}
                        onClick={e => { e.stopPropagation(); void revert(row, i, 'hourlyRate'); }}>
                        Revert wage
                      </button>
                    )}
                  </div>
                )}
              </td>
            );
          })}
          <td colSpan={totalColSpan} className="text-right py-1.5 px-4 text-xs text-slate-500 tabular-nums align-top">
            {row.total !== null && row.total !== undefined ? formatCurrency(Math.abs(row.total), true, currency) : ''}
          </td>
        </tr>
      ))}
      {emptyCount > 0 && (
        <tr className="fv3-r-driver fv3-r-driver-foot border-b border-slate-100" style={{ background: 'var(--lodge-onyx-50, #FBFBFD)' }}>
          <td
            colSpan={stickyColSpan}
            className={`fv3-driver-label ${isBulkMode ? '' : 'sticky left-0'} py-1 px-4 z-20`}
            style={{ background: 'var(--lodge-onyx-50, #FBFBFD)', paddingLeft: '4.5rem' }}
          >
            <button
              type="button"
              onClick={e => { e.stopPropagation(); setShowEmpty(v => !v); }}
              className="flex items-center gap-1 text-[11px] font-semibold hover:underline"
              style={{ color: 'var(--lodge-orange-400)' }}
            >
              {showEmpty
                ? 'Show less'
                : `Show ${emptyCount} more without a value`}
            </button>
          </td>
          <td colSpan={MONTHS.reduce((n, _, i) => n + monthColSpan(i), 0) + totalColSpan} />
        </tr>
      )}
      {editor && (
        <RateEditorPopover
          state={editor}
          scope={scope}
          onScope={setScope}
          draft={draft}
          onDraft={setDraft}
          onApply={() => { void apply(); }}
          onCancel={closeEditor}
          onRevert={() => {
            const row = rows.find(r => r.key === editor.rowKey);
            if (row) void revert(row, editor.monthIndex, editor.field === 'hourlyRate' ? 'hourlyRate' : row.field);
          }}
          scopeLabels={scopeLabels}
          disabledScopes={disabledScopes}
        />
      )}
    </>
  );
}
